import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import i18next from 'i18next';
import { clientBasketManagementApi, clientOrderControllerApi } from '@api/api-config.ts';
import { useUser } from '@hooks/useUser.ts';
import dayjs from 'dayjs';
import { useToastStore } from '@stores/toastStore';

import {
  AttachmentResponseDTO,
  ClientAddressDTO,
  ClientBasketItemDTO,
  ClientBasketResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  CreateOrderSupplierOrderRequest,
  DeliveryCalendarDay,
  SupplierAddressDTO,
} from '@api/generated/search-service';
import { WizardStep } from '@components/ui/Wizard.tsx';
import { CartItemDto } from '@deprecated/model.ts';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

type CartItemDtoWithCreatedAt = CartItemDto & { createdAt: string };

// Definice kroků wizardu
export const getWizardSteps = (): WizardStep[] => [
  { id: 'basket', name: i18next.t('client:basket.steps.basket'), number: 1 },
  { id: 'delivery-payment', name: i18next.t('client:basket.steps.order'), number: 2 },
  { id: 'recap', name: i18next.t('client:basket.steps.summary'), number: 3 },
];

const wizardStepIds = ['basket', 'delivery-payment', 'recap'];

export interface Order {
  supplierId: number;
  supplierName: string;
  orderAmount: BasketPrice;
  supplierItems: ClientBasketItemDTO[];
  selectedDeliveryDay?: DeliveryCalendarDay;
  paymentMethod: 'INVOICE';
  deliveryMethod: 'BY_SUPPLIER';
  internalNumber?: string;
  note?: string;
  attachments?: AttachmentResponseDTO[];
  blindOrder: boolean;
  supplierAddress: SupplierAddressDTO;
  selectedDeliveryAddress: ClientAddressDTO;
  deliveryAddresses: ClientAddressDTO[];
  contactPersonName?: string;
  contactPersonPhone?: string;
}

interface BasketPrice {
  priceWithoutVat: number;
  priceWithVat: number;
}

type SetItemCountParams = {
  productId: number;
  priceVariantId: number;
  quantity: number;
  supplierId: number;
  productName?: string;
};

type CartState = {
  basket: ClientBasketResponse;
  wizardStepId?: string;
  items: CartItemDtoWithCreatedAt[];
  delivery4Supplier: { [supplierId: number]: DeliveryCalendarDay | undefined };
  // actually selected items
  selectedProductIds: { [supplierId: number]: number[] };
  // user select all from the supplier - if another item comes, we can use this to select it automatically
  isSelectedAllForSupplier: { [supplierId: number]: boolean | undefined };
  selectedOrders: { [supplierId: number]: Order };
  submitOrderResult: CreateOrderResponse | undefined;
  internalNumberErrors: { [supplierId: number]: string | undefined };
  loading: boolean;
  error?: string;
  loaded: boolean;

  startProcess: () => void;

  // ⬇️ nově pro otevírání/zavírání
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  // ⬇️ datum doručení a blokované dny
  deliveryDate: Date;
  blockedDates: Date[];
  setDeliveryDate: (date: Date) => void;
  setBlockedDates: (dates: Date[]) => void;

  toggleProduct: (supplierId: number, productId: number, selected: boolean) => void;
  toggleAllForSupplier: (supplierId: number, selected: boolean) => void;

  onChangedAttachments: (supplierId: number, attachments: any[]) => void;
  onChangedNote: (supplierId: number, note: string) => void;
  onChangedInternalNumber: (supplierId: number, internalNumber: string) => void;
  validateInternalNumber: (supplierId: number) => Promise<void>;
  onChangedDeliveryAddress: (supplierId: number, address: ClientAddressDTO) => void;
  onChangedContactPerson: (supplierId: number, name: string, phone: string) => void;
  onDeleteFile: (supplierId: number, fileUuid: string) => void;

  refresh: () => Promise<void>;
  setItemCount: (p: SetItemCountParams) => Promise<void>;
  acceptNewPrice: (cartItemId: number, newPriceVariantId: number) => Promise<void>;
  deleteCartItem: (cartItemId: number, productName?: string) => Promise<void>;
  deleteBasket: () => Promise<void>;
  setDeliveryDayForSupplier: (supplierId: number, day: DeliveryCalendarDay | undefined) => void;
  submitOrder: () => Promise<void>;
  nextStep?: () => void;
  previousStep?: (stepId: string | undefined) => void;
};

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
  items: [],
  wizardStepId: 'basket', // FIXME - enum
  basket: {} as ClientBasketResponse,
  delivery4Supplier: {},
  selectedProductIds: {},
  isSelectedAllForSupplier: {},
  selectedOrders: {},
  loaded: false,
  loading: true,
  error: undefined,
  submitOrderResult: undefined,
  internalNumberErrors: {},

  // ⬇️ defaultně zavřeno
  isOpen: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set(s => ({ isOpen: !s.isOpen })),

  // ⬇️ defaultně D+1 (zítra)
  deliveryDate: dayjs().add(1, 'day').toDate(),
  blockedDates: [],
  setDeliveryDate: (date: Date) => set({ deliveryDate: date }),
  setBlockedDates: (dates: Date[]) => set({ blockedDates: dates }),

  startProcess: () => {
    set({
      items: [],
      wizardStepId: 'basket', // FIXME - enum
      basket: {} as ClientBasketResponse,
      delivery4Supplier: {},
      selectedProductIds: {},
      isSelectedAllForSupplier: {},
      selectedOrders: {},
      loaded: false,
      loading: true,
      error: undefined,
      submitOrderResult: undefined,
      internalNumberErrors: {},
    });
    get().refresh();
  },
  onChangedNote: (supplierId: number, note: string) => {
    const currentOrders = get().selectedOrders;
    const updatedOrder = {
      ...currentOrders[supplierId],
      note,
    };
    const updatedOrders = {
      ...currentOrders,
      [supplierId]: updatedOrder,
    };
    set({ selectedOrders: updatedOrders });
  },
  onChangedContactPerson: (supplierId: number, name: string, phone: string) => {
    const currentOrders = get().selectedOrders;
    const updatedOrder = {
      ...currentOrders[supplierId],
      contactPersonName: name || undefined,
      contactPersonPhone: phone || undefined,
    };
    const updatedOrders = {
      ...currentOrders,
      [supplierId]: updatedOrder,
    };
    set({ selectedOrders: updatedOrders });
  },
  deleteBasket: async () => {
    set({ loading: true, error: undefined });
    try {
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;
      await clientBasketManagementApi.clearBasket({ organizationId, portalId });
      set({ loading: false });
      useToastStore.getState().addToast({
        severity: 'success',
        summary: i18next.t('client:basket.toasts.cartDeleted'),
        detail: i18next.t('client:basket.toasts.cartDeletedDetail'),
      });
      await get().refresh();
    } catch (e) {
      set({ error: i18next.t('client:basket.toasts.cartDeleteFailed'), loading: false });
      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:common.error'),
        detail: i18next.t('client:basket.toasts.cartDeleteFailed'),
      });
    }
  },
  onChangedInternalNumber: (supplierId: number, internalNumber: string) => {
    const currentOrders = get().selectedOrders;
    const updatedOrder = {
      ...currentOrders[supplierId],
      internalNumber,
    };
    const updatedOrders = {
      ...currentOrders,
      [supplierId]: updatedOrder,
    };
    // Clear error when user types
    const updatedErrors = { ...get().internalNumberErrors, [supplierId]: undefined };
    set({ selectedOrders: updatedOrders, internalNumberErrors: updatedErrors });
  },
  validateInternalNumber: async (supplierId: number) => {
    const order = get().selectedOrders[supplierId];
    const internalNumber = order?.internalNumber;
    if (!internalNumber || internalNumber.trim() === '') {
      set({ internalNumberErrors: { ...get().internalNumberErrors, [supplierId]: undefined } });
      return;
    }
    try {
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;
      const response = await clientOrderControllerApi.checkOrderNumberAvailable({
        organizationId,
        portalId,
        orderNumber: internalNumber,
      });
      const error = response.data.available ? undefined : i18next.t('client:basket.toasts.internalNumberUsed');
      set({ internalNumberErrors: { ...get().internalNumberErrors, [supplierId]: error } });
    } catch {
      // Silently ignore - validation will happen on submit
    }
  },
  onChangedAttachments: (supplierId: number, attachments: any[]) => {
    const currentOrders = get().selectedOrders;
    const updatedOrder = {
      ...currentOrders[supplierId],
      attachments,
    };
    const updatedOrders = {
      ...currentOrders,
      [supplierId]: updatedOrder,
    };
    set({ selectedOrders: updatedOrders });
  },
  onChangedDeliveryAddress: (supplierId: number, address: ClientAddressDTO) => {
    const currentOrders = get().selectedOrders;
    const updatedOrder = {
      ...currentOrders[supplierId],
      selectedDeliveryAddress: address,
    };
    const updatedOrders = {
      ...currentOrders,
      [supplierId]: updatedOrder,
    };
    set({ selectedOrders: updatedOrders });
  },
  onDeleteFile: (supplierId: number, fileUuid: string) => {
    const currentOrders = get().selectedOrders;
    const currentAttachments = currentOrders[supplierId].attachments || [];
    const updatedAttachments = currentAttachments.filter(att => att.uuid !== fileUuid);
    const updatedOrder = {
      ...currentOrders[supplierId],
      attachments: updatedAttachments,
    };
    const updatedOrders = {
      ...currentOrders,
      [supplierId]: updatedOrder,
    };
    set({ selectedOrders: updatedOrders });
  },
  nextStep: () => {
    const currentStepId = get().wizardStepId;
    if (!currentStepId) return;

    const currentIndex = wizardStepIds.indexOf(currentStepId);
    if (currentIndex === -1 || currentIndex === wizardStepIds.length - 1) return;

    const nextStepId = wizardStepIds[currentIndex + 1];
    set({ wizardStepId: nextStepId });
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  },
  previousStep: (stepId: string | undefined) => {
    const currentStepId = get().wizardStepId;
    if (!currentStepId) return;

    const preferredStepIndex = stepId ? wizardStepIds.indexOf(stepId) : -1;

    const currentIndex = wizardStepIds.indexOf(currentStepId);
    if (currentIndex <= 0) return;

    const previousStepId = wizardStepIds[preferredStepIndex !== -1 ? preferredStepIndex : currentIndex - 1];

    set({ wizardStepId: previousStepId });
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  },
  toggleProduct: (supplierId: number, productId: number, selected: boolean) => {
    // uncheck the select all for supplier
    const currentSelectedAll = get().isSelectedAllForSupplier;
    const updatedSelectedAll = {
      ...currentSelectedAll,
      [supplierId]: undefined,
    };
    // remove the product from the selection
    const selectedProductIds = processProductSelection(get().selectedProductIds, supplierId, productId, selected);

    const selectedOrders = computeOrders(get().basket, selectedProductIds, get().delivery4Supplier);

    set({ selectedProductIds, isSelectedAllForSupplier: updatedSelectedAll, selectedOrders });
  },
  toggleAllForSupplier: (supplierId: number, selected: boolean) => {
    // uncheck the select all for supplier
    const currentSelectedAll = get().isSelectedAllForSupplier;
    const updatedSelectedAll = {
      ...currentSelectedAll,
      [supplierId]: selected,
    };
    let selectedProductIds = { ...get().selectedProductIds };

    const newSelectedProductIds = processSelection(updatedSelectedAll, selectedProductIds, get().basket);

    const selectedOrders = computeOrders(get().basket, newSelectedProductIds, get().delivery4Supplier);

    set({ selectedProductIds: newSelectedProductIds, isSelectedAllForSupplier: updatedSelectedAll, selectedOrders });
  },
  refresh: async () => {
    set({ loading: true, error: undefined });

    const portalId = useOrgPortal.getState().portalId;
    const organizationId = useOrgPortal.getState().orgId;
    const userId = useUser.getState().beUser?.id || 0;
    const delivery4Supplier = get().delivery4Supplier;

    try {
      const res = await clientBasketManagementApi.recomputeBasket({ organizationId, portalId, requestBody: mapDeliveryDates(delivery4Supplier) });

      const newDelivery4Supplier = res.data.supplierItems
        .map(t => ({
          [t.supplierId]: t.firstDeliveryDate,
        }))
        .filter(t => t !== undefined)
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});

      // if there is new supplier we have to tell, that the user did not change selection
      const defaults = addMissingDefaultForSupplier(res.data, get().isSelectedAllForSupplier);
      const selectedProductIds = processSelection(defaults, get().selectedProductIds, res.data);

      // Zachovat uživatelská data z předchozích objednávek
      const oldSelectedOrders = get().selectedOrders;
      const newSelectedOrders = computeOrders(res.data, selectedProductIds, newDelivery4Supplier);

      // Merge user data (poznámky, přílohy, adresy) z old do new
      const mergedSelectedOrders: { [supplierId: number]: Order } = {};
      for (const supplierId in newSelectedOrders) {
        const newOrder = newSelectedOrders[supplierId];
        const oldOrder = oldSelectedOrders[supplierId];

        mergedSelectedOrders[supplierId] = {
          ...newOrder,
          note: oldOrder?.note,
          internalNumber: oldOrder?.internalNumber,
          attachments: oldOrder?.attachments || [],
          selectedDeliveryAddress: oldOrder?.selectedDeliveryAddress || newOrder.selectedDeliveryAddress,
          contactPersonName: oldOrder?.contactPersonName,
          contactPersonPhone: oldOrder?.contactPersonPhone,
        };
      }

      set({
        basket: res.data,
        loading: false,
        loaded: true,
        items: map(res.data, userId, portalId, organizationId),
        delivery4Supplier: newDelivery4Supplier,
        selectedProductIds: selectedProductIds,
        isSelectedAllForSupplier: defaults,
        selectedOrders: mergedSelectedOrders,
      });
    } catch (e) {
      set({ error: i18next.t('client:basket.toasts.loadCartFailed'), loading: false });
    }
  },
  setItemCount: async p => {
    const portalId = useOrgPortal.getState().portalId;
    const organizationId = useOrgPortal.getState().orgId;
    set({ loading: true, error: undefined });
    try {
      await clientBasketManagementApi.setQuantityForProduct({
        organizationId,
        portalId,
        setQuantityRequest: {
          productId: p.productId,
          priceVariantId: p.priceVariantId,
          quantity: p.quantity,
          supplierId: p.supplierId,
        },
      });

      useToastStore.getState().addToast({
        severity: 'success',
        summary: i18next.t('client:basket.toasts.quantityUpdated'),
        detail: i18next.t('client:basket.toasts.quantityUpdatedDetail', { name: p.productName }),
      });
      await get().refresh();
    } catch (e: any) {
      const serverMessage = e?.response?.data?.description;
      const detail = serverMessage || i18next.t('client:basket.toasts.cartOperationFailed');
      set({ error: detail, loading: false });
      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:common.error'),
        detail,
      });
    }
  },
  acceptNewPrice: async (cartItemId: number, newPriceVariantId: number) => {
    set({ loading: true, error: undefined });
    try {
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;
      await clientBasketManagementApi.acceptNewPrice({ organizationId, portalId, acceptNewPriceRequest: { cartItemId, newPriceVariantId } });
      await get().refresh();
    } catch (e: any) {
      const detail = e?.response?.data?.description || i18next.t('client:basket.toasts.cartOperationFailed');
      set({ error: detail, loading: false });
      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:common.error'),
        detail,
      });
    }
  },
  deleteCartItem: async (cartItemId: number, productName?: string) => {
    set({ loading: true, error: undefined });
    try {
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;
      await clientBasketManagementApi.removeProductFromBasket({ cartItemId, portalId, organizationId });
      set({ loading: true, error: undefined });
      useToastStore.getState().addToast({
        severity: 'success',
        summary: i18next.t('client:basket.toasts.productRemoved'),
        detail: productName ? i18next.t('client:basket.toasts.productRemovedDetail', { name: productName }) : i18next.t('client:basket.toasts.productRemovedGeneric'),
      });
      await get().refresh();
    } catch (e) {
      set({ error: i18next.t('client:basket.toasts.removeItemFailed'), loading: false });
      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:common.error'),
        detail: i18next.t('client:basket.toasts.removeItemFailed'),
      });
    }
  },
  setDeliveryDayForSupplier: (supplierId: number, day: DeliveryCalendarDay | undefined) => {
    const current = get().delivery4Supplier;
    const updated = {
      ...current,
      [supplierId]: day,
    };
    set({ delivery4Supplier: updated });
    get().refresh();
  },
  submitOrder: async () => {
    const portalId = useOrgPortal.getState().portalId;
    const organizationId = useOrgPortal.getState().orgId;
    const { selectedOrders } = get();

    const ordersToSubmit = Object.values(selectedOrders).filter(order => order.supplierItems.length > 0);

    // Bez doručovací adresy nelze objednávku odeslat (deliveryAddressId je na API povinné)
    if (ordersToSubmit.some(order => !order.selectedDeliveryAddress)) {
      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:common.error'),
        detail: i18next.t('client:basket.toasts.deliveryAddressRequired'),
      });
      return;
    }

    // Převést selectedOrders na formát pro API
    const supplierOrders = ordersToSubmit
      .map(
        order =>
          ({
            supplierId: order.supplierId,
            items: order.supplierItems.map(item => ({
              productId: item.productId,
              priceVariantId: item.garancy?.priceVariantId || 0,
              quantity: item.quantity,
            })),
            attachmentUuids: order.attachments?.map(att => att.uuid) || [],
            selectedDeliveryDate: order.selectedDeliveryDay?.date || '',
            note: order.note,
            internalNumber: order.internalNumber,
            deliveryAddressId: order.selectedDeliveryAddress.id,
            contactPersonName: order.contactPersonName,
            contactPersonPhone: order.contactPersonPhone,
          }) as CreateOrderSupplierOrderRequest,
      );

    const createOrderRequest: CreateOrderRequest = {
      supplierOrders,
    };

    try {
      const response = await clientOrderControllerApi.createOrder({
        organizationId,
        portalId,
        createOrderRequest,
      });

      /*      useToastStore.getState().addToast({
        severity: 'success',
        summary: 'Objednávka odeslána',
        detail: 'Objednávka byla úspěšně odeslána',
      });*/

      set({ submitOrderResult: response.data });

      // Refreshnout košík po úspěšném odeslání
      await get().refresh();
    } catch (error) {
      useToastStore.getState().addToast({
        severity: 'error',
        summary: i18next.t('client:basket.toasts.orderSendError'),
        detail: i18next.t('client:basket.toasts.orderSendFailed'),
      });
      throw error;
    }
  },
    }),
    {
      name: 'cart-wizard-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        wizardStepId: state.wizardStepId,
        selectedProductIds: state.selectedProductIds,
        delivery4Supplier: state.delivery4Supplier,
        selectedOrders: state.selectedOrders,
        isSelectedAllForSupplier: state.isSelectedAllForSupplier,
      }),
      version: 2, // Zvýšil jsem verzi kvůli změně struktury
    },
  ),
);

const map = (basket: ClientBasketResponse, userId: number, portalId: number, organizaionId: number): CartItemDtoWithCreatedAt[] => {
  return basket.supplierItems
    .flatMap(supplier =>
      supplier.items.map(
        item =>
          ({
            id: item.id,
            createdAt: item.createdAt,
            productId: item.productId,
            userId: userId,
            organizationId: organizaionId,
            supplierId: supplier.supplierId,
            portalId: portalId,
            priceVariantId: item.garancy?.priceVariantId || 0,
            quantity: item.quantity,
            cartCatalogDataDto: {
              id: 1,
              sellUnit: `${item.sellUnit}`,
              minimumQty: item.minimumQty,
              multiply: item.multiply,
            },
            cartPriceVariantDto: {
              id: item.garancy?.priceVariantId || 0,
              price: item.garancy?.price || 0,
              vat: item.vat ?? 0,
            },
            cartSupplierDto: {
              id: supplier.supplierId,
              name: supplier.supplierName,
              // Limit je vlastnost konkretniho zavozoveho dne, ne dodavatele — proto
              // se bere z `firstDeliveryDate`. `undefined` znamena "na ten den bez limitu".
              orderLimit: supplier.firstDeliveryDate?.limit ?? null,
              deliveryDate: supplier.firstDeliveryDate?.date ?? null,
              imageUrl: null,
            },
            cartProductDto: {
              id: item.productId,
              name: item.productName,
              ecid: item.productEcid,
              imageUrl: item.productImageUrl || null,
              alternativeCount: undefined,
              isCheapest: undefined,
              hasCheaperAlternative: undefined,
            },
            cartCategoryDto: {
              id: item.categoryId,
              ecid: item.categoryEcid,
              name: item.categoryName,
            },
          }) as CartItemDtoWithCreatedAt,
      ),
    )
    .sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());
};

const mapDeliveryDates = (delivery4Supplier: { [supplierId: number]: DeliveryCalendarDay | undefined }) => {
  const result: { [supplierId: number]: string } = {};
  for (const supplierId in delivery4Supplier) {
    const date = delivery4Supplier[supplierId];
    if (!date) {
      continue;
    }
    result[supplierId] = date.date;
  }
  return result;
};
// automatický fetch při startu
//useCart.getState().refresh();

/**
 * this mehods compute the initial all seleection for suppliers in the basket
 */
const addMissingDefaultForSupplier = (
  currentBasket: ClientBasketResponse,
  currentSelection: { [supplier: number]: boolean | undefined },
): { [supplier: number]: boolean | undefined } => {
  let selection: { [supplier: number]: boolean | undefined } = currentSelection;

  currentBasket.supplierItems.forEach(supplier => {
    if (!(supplier.supplierId in selection)) {
      selection[supplier.supplierId] = true;
    }
  });

  return selection;
};

const processProductSelection = (
  currentSelection: { [supplierId: number]: number[] },
  supplierId: number,
  productId: number,
  selected: boolean,
): { [supplierId: number]: number[] } => {
  const updatedSelection = { ...currentSelection };
  const selectedProducts = new Set(updatedSelection[supplierId] || []);

  if (selected) {
    selectedProducts.add(productId);
  } else {
    selectedProducts.delete(productId);
  }

  updatedSelection[supplierId] = Array.from(selectedProducts);
  return updatedSelection;
};

const processSelection = (
  currentSelectedAll: { [supplierId: number]: boolean | undefined },
  currentSelection: { [supplierId: number]: number[] },
  basket: ClientBasketResponse,
): { [supplierId: number]: number[] } => {
  // if the select all is set, we enable all product for the supplier
  // if the select all is set to false, remove all products for the supplier from selection
  // if the select all is undefined, we do not change anything

  let updatedSelection = { ...currentSelection };

  for (const supplier of basket.supplierItems) {
    const isSelectedAll = currentSelectedAll[supplier.supplierId];
    if (isSelectedAll === true) {
      // select all products for this supplier
      updatedSelection[supplier.supplierId] = supplier.items.map(item => item.productId);
    } else if (isSelectedAll === false) {
      // remove all products for this supplier from selection
      updatedSelection[supplier.supplierId] = [];
    }
    // if undefined, do not change anything
  }

  return updatedSelection;
};

const computeOrders = (
  basket: ClientBasketResponse,
  selectedProducts: { [supplierId: number]: number[] },
  delivery4Supplier: { [supplierId: number]: DeliveryCalendarDay | undefined },
): { [supplierId: number]: Order } => {
  const orders: { [supplierId: number]: Order } = {};

  for (const supplier of basket.supplierItems) {
    const selectedProductIds = selectedProducts[supplier.supplierId] || [];
    let order = {
      selectedDeliveryDay: delivery4Supplier[supplier.supplierId],
      orderAmount: { priceWithoutVat: 0, priceWithVat: 0 },
      supplierId: supplier.supplierId,
      supplierItems: [],
      supplierName: supplier.supplierName,
      paymentMethod: 'INVOICE',
      deliveryMethod: 'BY_SUPPLIER',
      blindOrder: false,
      attachments: [],
      supplierAddress: supplier.supplierAddress,
      selectedDeliveryAddress: basket.deliveryAddresses[0],
      deliveryAddresses: basket.deliveryAddresses ?? [],
    } as Order;
    for (const item of supplier.items) {
      if (selectedProductIds.includes(item.productId)) {
        order = {
          ...order,
          supplierItems: [...(order.supplierItems || []), item],
          orderAmount: {
            priceWithoutVat: order.orderAmount.priceWithoutVat + (item.garancy?.price ?? 0) * item.quantity,
            priceWithVat: order.orderAmount.priceWithVat + (item.garancy?.price ?? 0) * item.quantity * 1.21,
          },
        };
      }
    }
    orders[supplier.supplierId] = order;
  }

  return orders;
};
